import { IUser } from '../../atoms';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import { ERROR } from '../../_constants';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import dayjs from 'dayjs';
import i18n from '../../translation/i18n';

// 상수 정의
const WIDGET_TYPES = {
  DDAY: '021', // 준공 D-DAY
  ACCIDENT: '028', // 금일 무사고 현황
  SAFETY_VIOLATION: '129', // 금일 출입금지
  ENTRY_VIOLATION: '130', // 금일 안전지적
  ATTEND: '131', // 금일 출역현황
  NEW_WORKER: '148', // 금일 신규근로자
  EXIT_WORKER: '149', // 퇴장 인원
  REMAINING_PERSONNEL: '150', // 실시간 잔류인원
} as const;

type WidgetType = (typeof WIDGET_TYPES)[keyof typeof WIDGET_TYPES];

interface WidgetInfo {
  dataKey?: string;
  title: string;
  value: number | string;
  unit: string;
  icon: string;
  text: string;
  path: string;
  color: string;
  req?: { hCd: string; sCd: string };
}

interface InfoCardProps {
  name: string;
  userInfo: IUser;
  uniqueKey: string;
  refetchInterval?: number;
}

const Root = styled.div`
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.5rem 2rem 1.5rem;

  .red {
    > div > div > span,
    > div > span,
    > span {
      color: var(--widget-status-red) !important;
    }
  }
  .yellow {
    > div > div > span,
    > div > span,
    > span {
      color: var(--widget-status-yellow) !important;
    }
  }
  .white {
    > div > div > span,
    > div > span,
    > span {
      color: var(--widget-text-primary) !important;
    }
  }

  .theme1 {
    > span {
      color: var(--widget-primary);
    }
    > .sub-text > span {
      color: var(--widget-primary);
    }
    > div > span {
      color: var(--widget-text-primary);
    }
  }

  .theme2 {
    > span {
      color: var(--widget-secondary);
    }

    > .sub-text > span {
      color: var(--widget-secondary);
    }
    > div {
      > span {
        color: var(--widget-text-primary);
      }
    }
  }

  > .widget-header {
    width: 100%;

    > .widget-title {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      gap: 1rem;
      font-size: var(--widget-font-size-xl);
      font-weight: var(--font-semibold);
      .sub-text {
        font-size: var(--widget-font-size-sm);
        color: var(--widget-text-tertiary);

        .sub-flex-box {
          display: flex;
          align-items: center;
          gap: 0.25rem;

          .material-symbols-outlined {
            font-size: 18px;
            color: var(--widget-text-tertiary);
          }
        }
      }
      > .flex-between {
        gap: 1rem;

        > div > span {
          font-size: var(--widget-font-size-2xl);
          font-weight: var(--font-semibold);
          color: var(--text-primary);
        }
      }

      .icon-box {
        width: 1.5rem;
        height: 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
        }
      }
    }
  }

  > .widget-body {
    flex: 1;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;

    > div {
      display: flex;
      align-items: flex-end;
      gap: 0.25rem;
      align-items: baseline;

      > :nth-child(1) {
        font-size: var(--widget-font-size-6xl);
        font-weight: var(--font-bold);
      }
      > :nth-child(2) {
        font-size: var(--widget-font-size-2xl);
      }
    }
  }

  .animate-ping-box {
    position: relative;
    left: 0;
    top: -6px;
    margin-right: 0.75rem;
  }

  .dot-ping {
    position: absolute;
    left: 0;
    top: 0;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background-color: var(--widget-status-red);
  }

  .animate-ping {
    position: absolute;
    left: 0;
    top: 0;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background-color: var(--widget-status-red);
    animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  @keyframes ping {
    75% {
      transform: scale(2);
      opacity: 0;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

// 유틸리티 함수들
const formatNumber = (value: number | string): string => {
  return Number(value).toLocaleString();
};

const formatDate = (date: dayjs.Dayjs): string => {
  return `${date.format('MM')}월 ${date.format('DD')}일 ${date.format('ddd').charAt(0).toUpperCase()}요일`;
};

const isWidgetEnabled = (uniqueKey: string, hCd: string, sCd: string): boolean => {
  return !!hCd && !!sCd && sCd !== '00000' && Object.values(WIDGET_TYPES).includes(uniqueKey as WidgetType);
};

export const InfoCard = ({ name, userInfo, uniqueKey, refetchInterval }: InfoCardProps) => {
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;

  const { data, isLoading, isError } = useQuery(['infoCardGet', userInfo, uniqueKey], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(refetchInterval && { refetchInterval }),
    // enabled: isWidgetEnabled(uniqueKey, hCd, sCd),
  });

  // 위젯 정보 가져오기 함수들
  const getSafetyViolationInfo = (): WidgetInfo => ({
    dataKey: 'pointCount',
    title: '출입금지',
    value: 0,
    unit: '명',
    icon: 'block',
    text: '',
    req: { hCd, sCd },
    path: '/safe/pointblackcontents',
    color: 'red',
  });

  const getEntryViolationInfo = (): WidgetInfo => ({
    dataKey: 'blackCount',
    title: '안전지적',
    value: 0,
    unit: '명',
    icon: 'warning',
    text: '',
    req: { hCd, sCd },
    path: '/safe/pointblackcontents',
    color: 'yellow',
  });

  const getInfo = (): WidgetInfo => {
    switch (uniqueKey) {
      case WIDGET_TYPES.SAFETY_VIOLATION:
        return getSafetyViolationInfo();
      case WIDGET_TYPES.ENTRY_VIOLATION:
        return getEntryViolationInfo();
      default:
        return { dataKey: '', title: '', value: 0, unit: '', icon: '', text: '', path: '', color: '' };
    }
  };

  const getAccidentCount = async (): Promise<WidgetInfo> => {
    try {
      const res = await apiGet({ path: '/safe/accidentm/status', req: { hCd, sCd } });
      const { accidentAday, accidentRday } = res.data.data;
      return {
        dataKey: '',
        title: '무사고 달성 현황',
        value: accidentAday,
        unit: '일',
        icon: '',
        text:
          accidentRday === undefined || accidentRday === null || accidentRday === ''
            ? ''
            : accidentRday <= 0
            ? `<span class="theme2">${t('목표 달성일수까지 완료')}</span>`
            : `${t('달성일수까지')} <span class="theme2">${accidentRday}</span>${t('일 남음')}`,
        path: '',
        color: 'theme2',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('사고 현황 조회 실패:', error);
      throw error;
    }
  };

  const getDday = async (): Promise<WidgetInfo> => {
    try {
      const res = await apiGet({ path: '/site/info', req: { hCd, sCd } });
      const { siteInfo } = res.data.data;
      const today = dayjs();
      const startDate = dayjs(siteInfo.sDate);
      const finishDate = dayjs(siteInfo.fDate);
      const processDays = today.diff(startDate, 'day') || 0;
      const leftDays = Math.max(0, finishDate.diff(today, 'day')) || 0;
      const period = startDate.isValid() && finishDate.isValid() ? `${startDate.format('YY.MM.DD')} - ${finishDate.format('YY.MM.DD')}` : '';
      const dayUnit = t('일');

      return {
        dataKey: '',
        title: '준공 D-Day',
        value: `D-${leftDays}`,
        unit: '',
        icon: '',
        text: period
          ? `<div style='display: flex; align-items: center;'><div style='display: flex; align-items: center;gap: 0.2rem'><span class='material-symbols-rounded' style='font-size: 1.25rem; color: var(--widget-primary);'>calendar_month</span> ${period}</div><b style="padding: 0 0.3rem;">·</b><div style='display: flex; align-items: center;'><span style='color: var(--widget-primary)'>${processDays}</span>${dayUnit}</div></div>`
          : '',
        path: '',
        color: 'theme1',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('D-Day 조회 실패:', error);
      throw error;
    }
  };

  const getAttendCount = async (): Promise<WidgetInfo> => {
    try {
      const today = dayjs().format('YYYYMMDD');
      const res = await apiGet({ path: '/attend/day', req: { hCd, sCd, aDate1: today, aDate2: today } });
      const { attendList } = res.data.data;

      return {
        dataKey: '',
        title: '금일 출역',
        value: formatNumber(attendList.length),
        unit: '명',
        icon: '',
        text: `<div>${i18n.language === 'ko-KR' ? formatDate(dayjs()) : dayjs().locale('en').format('MM/DD(ddd)')}</div>`,
        // text: i18n.language === 'ko-KR' ? formatDate(dayjs()) : dayjs().locale('en').format('MM/DD(ddd)'),
        path: '',
        color: 'theme1',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw new Error('error');
    }
  };

  const getNewWorkerCount = async (): Promise<WidgetInfo> => {
    try {
      const today = dayjs().format('YYYYMMDD');
      const req = { wWorkstatus: 'I', wJobdate1: today, wJobdate2: today, hCd, sCd };
      const res = await apiGet({ path: '/worker', req });
      const { workerList } = res.data.data;

      return {
        dataKey: '',
        title: '신규 근로자',
        value: formatNumber(workerList.length),
        unit: '명',
        icon: '',
        text: `<div>${i18n.language === 'ko-KR' ? formatDate(dayjs()) : dayjs().locale('en').format('MM/DD(ddd)')}</div>`,
        path: '',
        color: 'theme1',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw new Error('error');
    }
  };

  const getAttendRealtimeCount = async (uKey: string): Promise<WidgetInfo> => {
    try {
      const isExitWorker = uKey === WIDGET_TYPES.EXIT_WORKER;
      const fInout = isExitWorker ? 'O' : 'I';
      const res = await apiGet({ path: '/attend/realtime', req: { hCd, sCd, fInout } });
      const { attendList } = res.data.data;

      return {
        dataKey: '',
        title: isExitWorker ? '퇴장 인원' : '실시간 잔류',
        value: formatNumber(attendList.length),
        unit: '명',
        icon: '',
        text: `<div class='flex-between'><div class='animate-ping-box'><div class='dot-ping'></div><div class='animate-ping'></div></div> ${
          i18n.language === 'ko-KR' ? `${dayjs().locale('ko').format('A hh:mm')} 기준` : dayjs().locale('en').format('MM/DD(ddd)')
        }</div></div>`,
        path: '',
        color: 'theme1',
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw new Error('error');
    }
  };

  const getWidgetData = async (): Promise<WidgetInfo> => {
    switch (uniqueKey) {
      case WIDGET_TYPES.DDAY:
        return getDday();
      case WIDGET_TYPES.ATTEND:
        return getAttendCount();
      case WIDGET_TYPES.ACCIDENT:
        return getAccidentCount();
      case WIDGET_TYPES.NEW_WORKER:
        return getNewWorkerCount();
      case WIDGET_TYPES.EXIT_WORKER:
      case WIDGET_TYPES.REMAINING_PERSONNEL:
        return getAttendRealtimeCount(uniqueKey);
      default:
        return getInfo();
    }
  };

  const fetchData = async (): Promise<WidgetInfo> => {
    try {
      const info = await getWidgetData();
      if (!info.path) return info;

      const res = await apiGet({ path: info.path, req: { hCd, sCd } });
      const rawValue = res?.data?.data[0]?.[info.dataKey || ''];

      return {
        ...info,
        value: formatNumber(rawValue || 0),
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      throw new Error('error');
    }
  };

  // 렌더링 함수들
  const renderIcon = (icon: string) => {
    if (!icon) return null;
    return (
      <div className='icon-box'>
        <span className='material-symbols-rounded'>{icon}</span>
      </div>
    );
  };

  const renderWidgetHeader = () => (
    <div className='widget-header'>
      <div className={`widget-title ${data?.color}`}>
        <div className='flex-between'>
          {renderIcon(data?.icon || '')}
          <span>{t(data?.title || name)}</span>
        </div>
        {data?.text && (
          // eslint-disable-next-line react/no-danger
          <div className='sub-text' dangerouslySetInnerHTML={{ __html: data.text }} />
        )}
      </div>
    </div>
  );

  const renderWidgetBody = () => (
    <div className='widget-body'>
      <div className={data?.color}>
        <span style={{ fontSize: String(data?.value).replace('D', '').length < 5 ? 'var(--widget-font-size-7xl)' : 'var(--widget-font-size-6xl)' }}>{data?.value ?? '0'}</span>
        <span>{t(data?.unit ?? '')}</span>
      </div>
    </div>
  );

  // 에러 및 로딩 상태 처리
  if (isError) return <IssueGuide text={ERROR} isImg={false} />;
  if (isLoading) return <SkeletonTable rows={1} rowHeight={40} />;

  return (
    <Root>
      {renderWidgetHeader()}
      {renderWidgetBody()}
    </Root>
  );
};
